import type { Agency, AgencyRef } from '@gat-ship/types'
import type { Ref } from 'vue'
import { useAgencyApiCalls } from '@gat-ship/lib/http-client'
import { useAgencySignalREvents } from '../../signalr/events/agency-signalR-events'

function mapAgencyToAgencyRef(agency: Agency): AgencyRef {
  return {
    id: agency.id,
    isInactive: agency.isInactive,
    level: agency.level,
    name: agency.name,
    parentGroupId: agency.parentGroup?.id,
    shortName: agency.shortName,
    treePath: agency.treePath
  }
}

export function useAgencyRefDataSource(
  agenciesRef: Ref<AgencyRef[] | undefined>
) {
  const { getAgencyRefsRequest } = useAgencyApiCalls()

  /**
   * Fetches the agencies ref.
   * @param options.forceFetch - Whether to force a fetch of the agencies ref. @default false
   * @param options.subscribeToEvents - Whether to subscribe to signalR events. @default true
   */
  async function fetchAgenciesRef(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }) {
    console.log('fetchAgenciesRef', agenciesRef.value)
    let result: AgencyRef[] | undefined = undefined
    if (agenciesRef.value === undefined || options?.forceFetch) {
      result = await getAgencyRefsRequest()
      setData(result)
    }
    const subscribeToEvents = options?.subscribeToEvents ?? true
    if (subscribeToEvents) {
      agencySignalREvents.subscribeToAgencyEvents()
    }
    return result
  }

  function setData(agencies: AgencyRef[]) {
    agenciesRef.value = agencies
  }

  function addAgency(agency: Agency) {
    if (!agenciesRef.value) {
      return
    }
    const agencyRef = mapAgencyToAgencyRef(agency)
    const newAgenciesRef = [...agenciesRef.value, agencyRef]
    setData(newAgenciesRef)
  }

  function updateAgency(agency: Agency) {
    if (!agenciesRef.value) {
      return
    }
    const agencyRef = mapAgencyToAgencyRef(agency)
    const newAgenciesRef = agenciesRef.value.map((a) =>
      a.id === agencyRef.id ? agencyRef : a
    )
    setData(newAgenciesRef)
  }

  const agencySignalREvents = useAgencySignalREvents({
    onAgencyAdded: (event) => {
      fetchAgenciesRef({ forceFetch: true })
    },
    onAgencyDeactivated: () => {
      fetchAgenciesRef({ forceFetch: true })
    },
    onAgencyReactivated: () => {
      fetchAgenciesRef({ forceFetch: true })
    },
    onAgencyNameChanged: () => {
      fetchAgenciesRef({ forceFetch: true })
    }
  })

  function unsubscribeFromEvents() {
    agencySignalREvents.unsubscribeFromAgencyEvents()
  }

  function subscribeToEvents() {
    agencySignalREvents.subscribeToAgencyEvents()
    return () => {
      unsubscribeFromEvents()
    }
  }

  return {
    agenciesRef: agenciesRef,
    fetchAgenciesRef,
    addAgency,
    updateAgency,
    /** Clears the store and unsubscribes from agency events.
     * @param options.unsubscribeFromEvents - Whether to unsubscribe from signalR events. @default true
     */
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      setData([])
      if (options?.unsubscribeFromEvents ?? true) {
        agencySignalREvents.unsubscribeFromAgencyEvents()
      }
    },
    subscribeToEvents,
    unsubscribeFromEvents
  }
}
